import React, { useState, useEffect, useRef } from 'react';
import { getAllProviders, deleteProvider, updateProvider } from '../../utils/firebase';
import './ServiceProvidersList.css';
import Modal from '../common/Modal';
import { auth } from '../../config/firebase';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { collection, addDoc, getDocs, query, where, setDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { addProvider } from '../../utils/firebase';
import { cleanupDuplicateProviders } from '../../utils/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { getAllCategories } from '../../utils/firebase';
import EditServiceProvider from './EditServiceProvider';

const ServiceProvidersList = ({ onDelete }) => {
  const [providers, setProviders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    phoneNumber: '',
    email: '',
    address: '',
    location: '',
    description: '',
    businessHours: {
      monday: { isOpen: false, open: '09:00', close: '17:00' },
      tuesday: { isOpen: false, open: '09:00', close: '17:00' },
      wednesday: { isOpen: false, open: '09:00', close: '17:00' },
      thursday: { isOpen: false, open: '09:00', close: '17:00' },
      friday: { isOpen: false, open: '09:00', close: '17:00' },
      saturday: { isOpen: false, open: '09:00', close: '17:00' },
      sunday: { isOpen: false, open: '09:00', close: '17:00' }
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [importType, setImportType] = useState('json');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStatus, setProcessStatus] = useState('');
  const fileInputRef = useRef(null);
  const [importError, setImportError] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirmImport, setShowConfirmImport] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importedFile, setImportedFile] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('json');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [cleanupProgress, setCleanupProgress] = useState(0);
  const [cleanupStatus, setCleanupStatus] = useState('');
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [dbCategories, setDbCategories] = useState([]);

  const fetchProviders = async () => {
    try {
      const currentScroll = window.scrollY;
      setLoading(true);
      const providersSnapshot = await getDocs(collection(db, 'providers'));
      const providersData = providersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProviders(providersData);
      setTimeout(() => {
        window.scrollTo(0, currentScroll);
      }, 0);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setError('Failed to fetch providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await getAllCategories();
        setDbCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
    fetchProviders();
  }, []);

  if (error) {
    return <div className="error-message">Error loading providers: {error}</div>;
  }

  const handleEditClick = (provider) => {
    setSelectedProvider(provider);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    fetchProviders();
    setMessage({ text: 'Provider updated successfully', type: 'success' });
    setIsEditModalOpen(false);
  };

  const handleDeleteClick = (provider) => {
    setProviderToDelete(provider);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setMessage({ text: 'You must be logged in to delete a provider', type: 'error' });
        return;
      }

      if (user.email !== 'admin@aroundu.co.za') {
        setMessage({ text: 'Insufficient permissions: Admin access required', type: 'error' });
        return;
      }

      await deleteProvider(providerToDelete.id);
      setMessage({ text: 'Provider deleted successfully', type: 'success' });
      fetchProviders();
      setDeleteModalOpen(false);
      setProviderToDelete(null);
    } catch (error) {
      setMessage({ text: 'Error deleting provider: ' + error.message, type: 'error' });
    }
  };

  const getCategories = () => {
    const providerCategories = [...new Set(providers.map(p => p.category))];
    
    const databaseCategories = dbCategories.map(cat => cat.name);
    
    const allCategories = [...new Set([...providerCategories, ...databaseCategories])].sort();
    
    return ['all', ...allCategories];
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.phoneNumber.includes(searchTerm) ||
                         (provider.email && provider.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || provider.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  }).sort((a, b) => a.name.localeCompare(b.name));

  const exportToJSON = () => {
    const dataStr = JSON.stringify(providers, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    saveAs(blob, `service-providers-${new Date().toISOString().split('T')[0]}.json`);
  };

  const exportToCSV = () => {
    const csvData = providers.map(provider => ({
      name: provider.name,
      category: provider.category,
      phoneNumber: provider.phoneNumber,
      email: provider.email,
      address: provider.address,
      location: provider.location,
      description: provider.description
    }));
    
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `service-providers-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    setShowConfirmImport(true);
    setImportError(null);
    setUploadProgress(0);
    setUploadStatus('');
  };

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      if (file.type === 'application/json') {
        // Handle JSON files
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = JSON.parse(e.target.result);
            resolve(Array.isArray(data) ? data : [data]);
          } catch (error) {
            reject(new Error('Invalid JSON format'));
          }
        };
        reader.onerror = () => reject(new Error('Error reading file'));
        reader.readAsText(file);
      } else if (file.type === 'text/csv') {
        // Handle CSV files
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.errors.length > 0) {
              reject(new Error('Invalid CSV format'));
            } else {
              resolve(results.data);
            }
          },
          error: (error) => reject(new Error('Error parsing CSV'))
        });
      } else {
        reject(new Error('Unsupported file format. Please use JSON or CSV'));
      }
    });
  };

  const prepareDataPreview = (data) => {
    // Filter out empty rows that might come from CSV
    const cleanData = data.filter(record => 
      record.name && record.category && record.phoneNumber && record.email
    );

    return {
      totalRecords: cleanData.length,
      validRecords: cleanData.length,
      invalidRecords: data.length - cleanData.length,
      sample: cleanData.slice(0, 3), // First 3 records for preview
      data: cleanData // Store full cleaned data for import
    };
  };

  const handleImportConfirm = async () => {
    try {
      setIsUploading(true);
      setUploadProgress(70);
      setUploadStatus('Uploading to database...');
      
      const importedData = importPreview.data;
      const totalRecords = importedData.length;
      let processedRecords = 0;
      let successCount = 0;
      let errorCount = 0;
      
      // Process records in batches
      const batchSize = 20;
      for (let i = 0; i < importedData.length; i += batchSize) {
        const batch = importedData.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (record) => {
          try {
            // Generate unique ID
            const uniqueId = `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            // Format provider data
            const providerData = {
              id: uniqueId,
              name: record.name || record.Name,
              category: record.category || record.Category,
              phoneNumber: record.phoneNumber || record.PhoneNumber || record['Phone Number'],
              email: record.email || record.Email || '',
              address: record.address || record.Address || '',
              description: record.description || record.Description || '',
              services: record.services || record.Services || [],
              reviews: [],
              rating: 0,
              verified: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            // Add to Firestore
            const providerRef = doc(db, 'providers', uniqueId);
            await setDoc(providerRef, providerData);
            successCount++;
          } catch (error) {
            console.error('Error adding record:', error);
            errorCount++;
          }
        }));
        
        processedRecords += batch.length;
        const progress = Math.min(70 + (processedRecords / totalRecords * 30), 100);
        setUploadProgress(progress);
        setUploadStatus(`Processed ${processedRecords} of ${totalRecords} records...`);
      }

      // Show final status
      if (errorCount > 0) {
        setUploadStatus(`Import completed with ${successCount} successes and ${errorCount} failures`);
      } else {
        setUploadStatus('Import completed successfully!');
      }
      setUploadProgress(100);
      
      // Refresh the providers list
      await fetchProviders();
      
      // Close modal after delay
      setTimeout(() => {
        setIsImportModalOpen(false);
        setUploadProgress(0);
        setUploadStatus('');
      }, 2000);

    } catch (error) {
      setImportError(`Import failed: ${error.message}`);
      setUploadStatus('Failed');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCleanupClick = async () => {
    try {
      // First authenticate
      await signInWithEmailAndPassword(
        auth,
        'admin@aroundu.co.za',
        '@dmin@2025'
      );

      // Then check for duplicates
      const duplicates = providers.filter((provider, index, self) =>
        self.findIndex(p => 
          p.name.toLowerCase() === provider.name.toLowerCase() && 
          p.category.toLowerCase() === provider.category.toLowerCase()
        ) !== index
      );
      
      setDuplicateCount(duplicates.length);
      
      if (duplicates.length > 0) {
        setShowDuplicateModal(true);
      } else {
        // Add modal overlay for the "no duplicates" message
        setShowDuplicateModal(true);
        setCleanupStatus('No duplicate providers found.');
        
        // Auto close the modal after 2 seconds
        setTimeout(() => {
          setShowDuplicateModal(false);
          setCleanupStatus('');
        }, 2000);
      }
    } catch (error) {
      console.error('Error in cleanup:', error);
      alert('Authentication failed. Please try again.');
    }
  };

  const handleConfirmCleanup = async () => {
    try {
      setIsCleaningUp(true);
      setCleanupProgress(0);
      setCleanupStatus('Starting cleanup...');

      // Perform the cleanup
      await cleanupDuplicateProviders();
      
      // Update progress
      setCleanupProgress(100);
      setCleanupStatus('Cleanup complete!');
      
      // Refresh the providers list
      await fetchProviders();
      
      // Close modal and reset states
      setTimeout(() => {
        setShowDuplicateModal(false);
        setIsCleaningUp(false);
        setCleanupProgress(0);
        setCleanupStatus('');
      }, 1500);

    } catch (error) {
      console.error('Error during cleanup:', error);
      setCleanupStatus('Error: ' + error.message);
      setIsCleaningUp(false);
      // Close modal after error
      setTimeout(() => {
        setShowDuplicateModal(false);
      }, 3000);
    }
  };

  const processImportData = (data) => {
    return data.map(record => ({
      name: record.name || '',
      category: record.category || '',
      phoneNumber: record.phoneNumber || '',
      phoneNumber2: record.phoneNumber2 || '',
      email: record.email || '',
      address: record.address || '',
      description: record.description || '',
      services: record.services || [],
      reviews: [],
      rating: 0,
      verified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }));
  };

  const uploadToFirebase = async (processedData) => {
    let batch = writeBatch(db);
    let count = 0;
    
    try {
      for (const record of processedData) {
        const uniqueId = `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const providerRef = doc(db, 'providers', uniqueId);
        
        batch.set(providerRef, record);
        count++;
        
        // Update progress more frequently
        const progress = Math.round((count / processedData.length) * 50) + 40; // 40-90%
        setUploadProgress(progress);
        setUploadStatus(`Uploading provider ${count} of ${processedData.length}...`);
        
        // Commit in smaller batches for better progress tracking
        if (count % 100 === 0) {
          await batch.commit();
          batch = writeBatch(db);
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for UI update
        }
      }
      
      // Commit any remaining records
      if (count % 100 !== 0) {
        await batch.commit();
      }
      
      return count;
    } catch (error) {
      throw new Error(`Upload failed at record ${count}: ${error.message}`);
    }
  };

  const startImport = async (event) => {
    event.preventDefault();
    
    if (isUploading || !selectedFile) return;
    
    try {
      setIsUploading(true);
      setUploadProgress(10);
      setUploadStatus('Reading file...');

      const data = await readFile(selectedFile);
      setUploadProgress(30);
      setUploadStatus('Processing data...');

      const processedData = processImportData(data);
      setUploadProgress(40);
      setUploadStatus('Starting database upload...');

      const importedCount = await uploadToFirebase(processedData);
      setUploadProgress(90);
      setUploadStatus('Refreshing provider list...');

      // Replace loadProviders with fetchProviders
      await fetchProviders();
      setUploadProgress(100);
      setUploadStatus(`Successfully imported ${importedCount} providers!`);

      // Clear file selection
      setSelectedFile(null);

      // Show success message for 2 seconds before closing
      setTimeout(() => {
        setUploadStatus('');
        setUploadProgress(0);
        setIsUploading(false);
      }, 2000);

    } catch (error) {
      console.error('Import error:', error);
      setUploadStatus(`Import failed: ${error.message}`);
      setUploadProgress(0);
      setIsUploading(false);
    }
  };

  const handleJSONFormat = () => {
    setSelectedFormat('json');
    // Clear any previously selected file
    setSelectedFile(null);
    setUploadStatus('');
    setUploadProgress(0);
  };

  const handleCSVFormat = () => {
    setSelectedFormat('csv');
    // Clear any previously selected file
    setSelectedFile(null);
    setUploadStatus('');
    setUploadProgress(0);
  };

  const handleImportClick = () => {
    if (!selectedFormat) {
      setUploadStatus('Please select a format (JSON or CSV) first');
      return;
    }

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = selectedFormat === 'json' ? '.json' : '.csv';
    fileInput.onchange = handleFileSelect;
    fileInput.click();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setUploadStatus('');
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleImportSuccess = () => {
    setImportedFile(null);
    setUploadProgress(0);
    setUploadStatus('');
    fetchProviders(); // Refresh the list
  };

  const handleConfirmDelete = async () => {
    try {
      await handleDelete(providerToDelete);
      setDeleteModalOpen(false);
      setProviderToDelete(null);
    } catch (error) {
      console.error('Error deleting provider:', error);
    }
  };

  console.log('Current state:', { loading, providers, error }); // Debug log

  if (loading) {
    console.log('Still loading...'); // Debug log
    return (
      <div className="loading">
        <i className="fas fa-spinner fa-spin"></i> Loading providers...
      </div>
    );
  }

  if (providers.length === 0) {
    return <div className="no-providers">No service providers found.</div>;
  }

  return (
    <div className="service-providers-list">
      <div className="data-management-section">
        <div className="section-header">
          <h2>
            <i className="fas fa-database"></i>
            Data Management
          </h2>
          <p>Import, export, and manage your service provider data</p>
        </div>

        <div className="data-section">
          <div className="section-box">
            <div className="section-title">EXPORT DATA</div>
            <div className="button-stack">
              <button className="export-button" onClick={exportToJSON}>
                <i className="fas fa-file-export"></i>
                Export as JSON
              </button>
              <button className="export-button" onClick={exportToCSV}>
                <i className="fas fa-file-export"></i>
                Export as CSV
              </button>
            </div>
          </div>

          <div className="section-box">
            <div className="section-title">IMPORT DATA</div>
            <div className="button-stack">
              <div className="format-selector">
                <button 
                  className={`format-button ${selectedFormat === 'json' ? 'active' : ''}`}
                  onClick={() => setSelectedFormat('json')}
                >
                  <i className="fas fa-file-code"></i>
                  JSON Format
                </button>
                <button 
                  className={`format-button ${selectedFormat === 'csv' ? 'active' : ''}`}
                  onClick={() => setSelectedFormat('csv')}
                >
                  <i className="fas fa-file-csv"></i>
                  CSV Format
                </button>
              </div>
              
              <button className="import-button" onClick={handleImportClick}>
                <i className="fas fa-file-import"></i>
                Import File
              </button>

              {selectedFile && (
                <div className="selected-file-box">
                  <div className="file-info">
                    <i className="fas fa-file-alt"></i>
                    <span className="file-name">{selectedFile.name}</span>
                    <span className="file-size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  
                  <button 
                    className="start-import-button"
                    onClick={startImport}
                    disabled={isUploading || !selectedFile}
                  >
                    {isUploading ? 'Importing...' : 'Start Import'}
                  </button>

                  {isUploading && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <div className={`upload-status ${
                        uploadProgress === 100 ? 'success' : 
                        uploadStatus.includes('failed') ? 'error' : ''
                      }`}>
                        {uploadStatus}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="section-box">
            <div className="section-title">DATA CLEANUP</div>
            <div className="button-stack">
              <button 
                className="cleanup-button"
                onClick={handleCleanupClick}
              >
                <i className="fas fa-broom"></i> Remove Duplicates
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="list-header">
        <h2>Service Providers</h2>
        <div className="list-controls">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            <option value="all">All Categories</option>
            {getCategories().map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All' : category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Loading providers...
        </div>
      ) : (
        <div className="providers-grid">
          {filteredProviders.map(provider => (
            <div key={provider.id} className="provider-card">
              <div className="provider-info">
                <h3 className="provider-name">{provider.name}</h3>
                <p><i className="fas fa-tag"></i> {provider.category}</p>
                <p><i className="fas fa-phone"></i> {provider.phoneNumber}</p>
                <p><i className="fas fa-envelope"></i> {provider.email}</p>
                <p><i className="fas fa-map-marker-alt"></i> {provider.address}</p>
              </div>
              <div className="button-container">
                <button 
                  className="edit-btn"
                  onClick={() => handleEditClick(provider)}
                >
                  <i className="fas fa-edit"></i> Edit
                </button>
                <button 
                  className="card-delete-btn"
                  onClick={() => handleDeleteClick(provider)}
                >
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {message.text && (
        <div className={`message ${message.type === 'error' ? 'error' : 'success'}`}>
          {message.text}
        </div>
      )}

      <EditServiceProvider
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        provider={selectedProvider}
        categories={getCategories()}
        onSuccess={handleEditSuccess}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={providerToDelete?.name || 'this provider'}
      />

      <Modal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        title="Import Preview"
      >
        <div className="import-preview-modal">
          {importError ? (
            <div className="import-error">
              <i className="fas fa-exclamation-circle"></i>
              <p>{importError}</p>
            </div>
          ) : (
            <>
              <div className="import-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Records</span>
                  <span className="stat-value">{importPreview?.totalRecords}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Valid Records</span>
                  <span className="stat-value success">{importPreview?.validRecords}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Invalid Records</span>
                  <span className="stat-value error">{importPreview?.invalidRecords}</span>
                </div>
              </div>

              <div className="preview-section">
                <h4>Sample Preview</h4>
                <div className="preview-table">
                  {importPreview?.sample.map((record, index) => (
                    <div key={index} className="preview-row">
                      <div className="preview-cell">
                        <strong>Name:</strong> {record.name}
                      </div>
                      <div className="preview-cell">
                        <strong>Category:</strong> {record.category}
                      </div>
                      <div className="preview-cell">
                        <strong>Phone:</strong> {record.phoneNumber}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setIsImportModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="confirm-btn"
                  onClick={handleImportConfirm}
                  disabled={isProcessing || importPreview?.validRecords === 0}
                >
                  {isProcessing ? 'Importing...' : 'Confirm Import'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {showDuplicateModal && (
        <div className="modal-overlay">
          <div className="cleanup-modal">
            {duplicateCount > 0 ? (
              // Show duplicate cleanup confirmation
              !isCleaningUp ? (
                <>
                  <h3>Remove Duplicate Providers</h3>
                  <p>Found {duplicateCount} providers with duplicate names and categories.</p>
                  <p>Are you sure you want to remove these duplicates?</p>
                  <div className="modal-buttons">
                    <button 
                      className="cancel-btn"
                      onClick={() => {
                        setShowDuplicateModal(false);
                        setCleanupStatus('');
                        setCleanupProgress(0);
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="confirm-btn"
                      onClick={handleConfirmCleanup}
                      disabled={isCleaningUp}
                    >
                      Yes, Remove Duplicates
                    </button>
                  </div>
                </>
              ) : (
                <div className="cleanup-progress">
                  <h3>Removing Duplicates</h3>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${cleanupProgress}%` }}
                    />
                  </div>
                  <div className="cleanup-status">{cleanupStatus}</div>
                </div>
              )
            ) : (
              // Show no duplicates message
              <div className="cleanup-progress">
                <h3>Duplicate Check</h3>
                <p>{cleanupStatus}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceProvidersList; 